import { RoadmapTask } from "@models";
import { Card, CircularProgress, Tooltip } from "@mui/material";
import { DocStatus, DocumentUIStatus } from "@types";
import { docStatusDictionary, docUIDictionary, getDocUIStatus } from "@utils";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { DocWarnings } from "src/components/DocViewer/components/DocWarnings";
import {
  useGetDevice,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetTasks,
  useGetUser,
} from "src/hooks/queries";
import { theme } from "./../../../config";

type Props = {
  task: RoadmapTask;
  docStatus: DocStatus | null;
  isLoading: boolean;
  onClick: () => void;
  incompleteTemplates?: string[];
};

export const TaskAccordionCard = ({
  task,
  docStatus,
  isLoading,
  onClick,
  incompleteTemplates,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const { deviceId = "", orgId = "" } = useParams();
  const { data: device } = useGetDevice({
    orgId,
    deviceId,
  });
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId, templatesConfig);
  const { data: user } = useGetUser();
  const { data: tasks } = useGetTasks(orgId, deviceId);

  if (
    docStatus === null ||
    !device ||
    !documents ||
    !user ||
    !templatesConfig ||
    !tasks
  )
    return;

  const docUIStatus = getDocUIStatus(task);

  const isTaskLocked = docUIStatus === DocumentUIStatus.LOCKED;

  const taskStatusInfo = docUIStatus
    ? docUIDictionary[docUIStatus]
    : docStatusDictionary[docStatus];

  const tooltipText = "Enroll in the beta to unlock this task.";

  return (
    <Tooltip
      title={tooltipText}
      followCursor
      {...(!isTaskLocked && { open: false })}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 6],
              },
            },
          ],
        },
      }}
    >
      <div
        className="flex items-center w-full"
        onClick={isTaskLocked ? undefined : onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative w-full">
          <Card
            data-testid={task.taskKey}
            id={task.taskKey}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${taskStatusInfo.borderColor}`,
              boxShadow: "none",
              padding: "10px",
              backgroundColor: "transparent",
              "&:hover": {
                background: `${theme.palette.grey[100]}`,
              },
              "& .doc-warnings-container": {
                maxHeight: 0,
                overflow: "hidden",
                transition: "max-height 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              },
              "& .doc-warnings-container.expanded": {
                maxHeight: "500px", // Large enough value to accommodate content
              },
            }}
          >
            <div className="flex items-start w-full gap-2">
              {isLoading ? <CircularProgress size={21} /> : taskStatusInfo.icon}
              <div>{task.title}</div>
            </div>
            <div
              className={`doc-warnings-container ${isHovered ? "expanded" : ""}`}
            >
              {incompleteTemplates && incompleteTemplates.length > 0 && (
                <div className="-m-2 mt-2">
                  <DocWarnings
                    templatesConfig={templatesConfig}
                    incompleteTemplates={incompleteTemplates}
                    size="small"
                    customTitle={`This task has`}
                    deviceId={deviceId}
                    device={device}
                    documents={documents}
                    user={user}
                    tasks={tasks}
                  />
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </Tooltip>
  );
};
